import React from 'react'
import Video from '@commons/video/Video'
import { selectPromotion } from '@content/helpers/src/gtmEvents'
import { getPBProps } from '@content/helpers/src/utils'
import viewportHelper from '@content/helpers/src/viewport.helpers'
import { ButtonRefs, MediaModal } from '@nespresso-design-system/components'
import classNames from 'classnames'
import { useRef, useState } from 'react'
import './Initiatives.scss'
import { InitiativesCardProps, InitiativesPBProps } from './initiatives.types'
import InitiativesCard from './subcomponents/InitiativesCard/InitiativesCard'
import useImpressionTracker from '@content/helpers/src/hooks/useImpressionTracker'
import SectionHeader from '@commons/section-header/SectionHeader'
import CarouselWrapper from '@commons/carousel-wrapper/CarouselWrapper'
import { ModalProvider } from '@commons/enhanced-modal/ModalContext'
import CTA from '@commons/cta/CTA.tsx'
import ModalRenderer from '@commons/enhanced-modal/ModalRenderer.tsx'

type InitiativesProps = {
    readonly data: string
}

const Initiatives = ({ data }: InitiativesProps) => {
    const [modalOpen, setModalOpen] = useState(false)
    const [selectedCard, setSelectedCard] = useState<InitiativesCardProps>(null)
    const ctaRef = useRef<ButtonRefs>(null)
    const pbProps = getPBProps<InitiativesPBProps>(data)
    const containerRef = useRef(null)
    const [selectedPromotionDetails, setSelectedPromotionDetails] =
        useState(null)

    const { campaign, copywriting, layout, config, translations } = pbProps

    useImpressionTracker(containerRef, campaign)

    const onCardSelected = (e: React.MouseEvent, idx: number) => {
        e.preventDefault()

        const card = { ...copywriting.cards[idx] }
        setSelectedCard(card)

        if (card.video) {
            setModalOpen(true)
            selectPromotion({
                ...campaign,
                cta_name: `Video - ${idx + 1} - ${card.heading}`
            })
        }
    }

    return (
        <div
            ref={containerRef}
            className={classNames(
                'initiatives',
                layout.padding_bottom || 'pb-8',
                layout.padding_top || 'pt-8'
            )}>
            <ModalProvider>
                <ModalRenderer
                    promotion_details={selectedPromotionDetails}
                    campaign={''}
                />
                <div className="initiatives__inner">
                    <div className="initiatives__content">
                        <SectionHeader
                            headline={copywriting.heading}
                            subhead={copywriting.subhead}
                            align="center"
                            alignMobile="left"
                            description={copywriting.description}
                            headlineTag="h2"
                        />
                        {copywriting?.ctaPrimary?.label &&
                            copywriting?.ctaSecondary?.label && (
                                <div className={'initiatives__content-cards'}>
                                    {copywriting?.ctaPrimary?.label && (
                                        <CTA
                                            {...copywriting.ctaPrimary}
                                            ref={ctaRef}
                                            campaign={campaign}
                                            defaultVariant="secondary"
                                            tracking_select_promotion={`menu chip - 1 - ${copywriting.ctaPrimary.label}`}
                                        />
                                    )}
                                    {copywriting?.ctaSecondary?.label && (
                                        <CTA
                                            {...copywriting.ctaSecondary}
                                            ref={ctaRef}
                                            iconPosition="leading"
                                            campaign={campaign}
                                            defaultVariant="secondary"
                                            tracking_select_promotion={`menu chip - 2 - ${copywriting.ctaSecondary.label}`}
                                        />
                                    )}
                                </div>
                            )}
                    </div>

                    {viewportHelper.is.mobile ? (
                        <div className="initiatives__slider">
                            <CarouselWrapper
                                carouselOptions={{
                                    navPosition: 'top',
                                    'data-slides': copywriting.cards.length
                                }}
                                items={copywriting.cards.map((card, idx) => (
                                    <InitiativesCard
                                        {...card}
                                        cardIndex={idx}
                                        setSelectedPromotionDetails={
                                            setSelectedPromotionDetails
                                        }
                                        onCardVideoSelected={event =>
                                            onCardSelected(event, idx)
                                        }
                                    />
                                ))}
                            />
                        </div>
                    ) : (
                        <div
                            className={classNames('initiatives__grid', {
                                'initiatives__grid--four':
                                    config.variant === 'four-cards' ||
                                    copywriting.variant === 'four-cards'
                            })}>
                            {copywriting.cards.map((card, idx) => (
                                <InitiativesCard
                                    {...card}
                                    key={`card-${idx}`}
                                    cardIndex={idx}
                                    isLarge={
                                        idx === 0 &&
                                        config.variant === 'three-cards'
                                    }
                                    setSelectedPromotionDetails={
                                        setSelectedPromotionDetails
                                    }
                                    onCardVideoSelected={event =>
                                        onCardSelected(event, idx)
                                    }
                                />
                            ))}
                        </div>
                    )}
                    {selectedCard?.video && (
                        <MediaModal
                            id="initiatives-modal"
                            size="large"
                            closeBtnA11yLabel={translations.closeBtnA11yTitle}
                            isOpen={modalOpen}
                            onClose={() => setModalOpen(false)}
                            contentSlot={
                                <Video
                                    url={selectedCard.video.url}
                                    urlMobile={selectedCard.video.url_mobile}
                                    auto_play={
                                        selectedCard.video.config.auto_play
                                    }
                                />
                            }
                        />
                    )}
                </div>
            </ModalProvider>
        </div>
    )
}

export default Initiatives
